import { Button} from "@sgi/gravity/Button";
import { ErrorAlert } from "@sgi/gravity/Alert";
import { Input } from "@sgi/gravity/Input";
import { Typography } from "@sgi/gravity/Typography";
import ButtonLink from "../Components/ButtonLink";
import { Box, HStack, VStack } from "@chakra-ui/react";
import {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import VerifiedIcon from "../Common/VerifiedIcon";
import { useSignUpProxy } from "../Custom Hooks/useSignUpProxy";
import { useWrapper } from "../Context/WrapperContext";
import { useVerifyRedirect } from "../Custom Hooks/useVerifyRedirect";
import CancelButton from "../Common/Form/CancelButton";
import { Link } from "@sgi/gravity";

export default function RegistrationSection() {
  const { increment } = useWrapper();

  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");

  const {
    sendCodeAction,
    verifyCodeAction,
    sendNewCodeAction,
    changeEmailAction,
    pageLevelError,
    verificationError,
    emailError,
  } = useSignUpProxy({ email, verificationCode });

  const [errorMessage, setErrorMessage] = useState("");
  useEffect(() => {
    let message = pageLevelError;
    if (message === "A user with the specified ID already exists. Please choose a different one.") {
      message = "A user with that email already exists.";
    }
    setErrorMessage(message);
  }, [pageLevelError]);

  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  useEffect(() => setEmailErrorMessage(emailError), [emailError]);

  const [codeErrorMessage, setCodeErrorMessage] = useState("");
  useEffect(() => setCodeErrorMessage(verificationError), [verificationError]);

  const [isVerified, setIsVerified] = useState(false);
  const [buttonText, setButtonText] = useState("");
  const [helperText, setHelperText] = useState("");
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hideCode, setHideCode] = useState(true);
  const [handleClick, setHandleClick] = useState(() => () => {});

  const verifiedStyles = isVerified && {
    borderColor: "seagreen",
    borderWidth: "2px",
    boxShadow: "0 0 0 3px darkseagreen",
  };

  const onVerifyChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setVerificationCode(value);
    setIsButtonDisabled(value === "");
    setCodeErrorMessage(
      e.target.checkValidity() ? "" : e.target.validationMessage
    );
  };

  const onVerificationCodeKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") verifyCodeAction.act();
  };

  const onEmailBlur = (e: any) => {
    if ((e.relatedTarget as HTMLElement)?.innerText === "Cancel") {
      e.relatedTarget.click();
      return;
    }
    checkEmail(e);
  };

  const onEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    setIsButtonDisabled(value === "");
    if (emailErrorMessage !== "") checkEmail(e);
  };

  const onEmailKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") sendCodeAction.act();
  };

  const checkEmail = (e: ChangeEvent<HTMLInputElement>) => {
    const isValid = e.target.checkValidity();
    if (isValid) {
      setEmailErrorMessage("");
      setIsButtonDisabled(false);
    } else {
      setEmailErrorMessage("Your email doesn't meet our requirement. Please enter a valid email.");
      setIsButtonDisabled(true);
    }
  };

  const handleResendCodeClick = useCallback(() => {
    sendNewCodeAction.act();
  }, [sendNewCodeAction]);

  const navigateToLogin = useVerifyRedirect("signIn");

  const emailInputRef = useRef<HTMLInputElement>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (sendCodeAction.visible) {
      setButtonText("Send verification code");
      setHelperText("We will send you a verification code");
      setIsButtonDisabled(true);
      setHandleClick(() => sendCodeAction.act);
      setHideCode(true);
      setIsVerified(false);
      setIsLoading(false);
      setEmail("");
      emailInputRef.current?.focus();
    } else if (verifyCodeAction.visible && sendNewCodeAction.visible) {
      setButtonText("Verify code");
      setHelperText("Verification code has been sent to your inbox");
      setIsButtonDisabled(true);
      setHandleClick(() => verifyCodeAction.act);
      setHideCode(false);
      setIsVerified(false);
      setIsLoading(false);
      setVerificationCode("");
      codeInputRef.current?.focus();
    } else if (changeEmailAction.visible) {
      setButtonText("Change email address");
      setHelperText("");
      setIsButtonDisabled(false);
      setHandleClick(() => changeEmailAction.act);
      setHideCode(true);
      setIsVerified(true);
      setIsLoading(false);
      increment();
    } else {
      setIsLoading(true);
      setErrorMessage("");
    }
  }, [
    changeEmailAction,
    increment,
    sendCodeAction,
    sendNewCodeAction,
    verifyCodeAction,
  ]);

  return (
    <Box style={{ zIndex: "200", position: "relative" }}>
      <Typography
        variant="body"
        mb={8}
        data-testid="already-have-an-account-test"
      >
        Already have an account? &nbsp;
        <ButtonLink
          testid="login-test"
          linkCaption="Sign in"
          handleClick={navigateToLogin}
        />
      </Typography>
      <Box data-testid="error-container">
        <Box data-testid="error-container">
          {errorMessage && (
            <ErrorAlert onClose={() => setErrorMessage("")}>
              {errorMessage}
            </ErrorAlert>
          )}
        </Box>
      </Box>
      <Input
        ref={emailInputRef}
        controlClassName="mb-3"
        label="Your email"
        name="Email"
        className={isVerified ? "validated" : ""}
        id="reg-email-field"
        type="email"
        style={{ backgroundColor: "white", ...verifiedStyles }}
        onChange={onEmailChange}
        onBlur={(e) => onEmailBlur(e)}
        onKeyDown={onEmailKeyDown}
        placeholder="email@example.com"
        pattern="^[a-zA-Z0-9!#$%&'+^_`\{\}~\-]+(?:\.[a-zA-Z0-9!#$%&'+^_`\{\}~\-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9\-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9\-]*[a-zA-Z0-9])?$"
        maxLength={73}
        error={emailErrorMessage}
        data-testid="email-input"
        isReadOnly={isVerified}
        inputRightElement={isVerified && <VerifiedIcon />}
        spellCheck={false}
        isRequired={true}
        value={email}
        autoComplete="username"
        formHelperText={helperText}
      />
      <Input
        ref={codeInputRef}
        controlClassName="my-3"
        controlStyleProps={hideCode ? { display: "none" } : undefined}
        label="Verification code"
        name="Verification"
        id="verify-code-field"
        type="text"
        style={{ backgroundColor: "white" }}
        onChange={onVerifyChange}
        onKeyDown={onVerificationCodeKeyDown}
        placeholder="Enter Verification code from email"
        error={codeErrorMessage}
        data-testid="code-input"
        isRequired={true}
        value={verificationCode}
        maxLength={10}
      />
      <VStack mt={6}>
        <Button
          isFullWidth
          onClick={handleClick}
          isLoading={isLoading}
          data-testid="action-button"
          isDisabled={isButtonDisabled}
        >
          {buttonText}
        </Button>
        <CancelButton />
        <HStack hidden={hideCode} textStyle="body">
          <span>Didn't receive the email?</span>
          <Link
            href="#"
            size="sm"
            onClick={handleResendCodeClick}
            data-testid="resend-code-button"
          >
            Resend code
          </Link>
        </HStack>
      </VStack>
    </Box>
  );
}
